import axios, {
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { logger } from "../utils/logger";
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from "../features/auth/services/authTokenStore";

const hostname = window.location.hostname;
const protocol = window.location.protocol;

let baseURL: string;

if (hostname === "192.168.13.94") {
  baseURL = "http://192.168.13.95:5000/api";
} else if (hostname === "localhost" || hostname === "127.0.0.1") {
  baseURL = "http://localhost:5000/api";
} else if (hostname === "192.168.12.52") {
  baseURL = "http://192.168.12.52:5000/api";
} else if (hostname === "gestorreservas679inn.com.ar") {
  baseURL = `${protocol}//${hostname}/api`;
} else {
  baseURL = `${protocol}//${hostname}:5000/api`;
}

// Debug: mostrar qué baseURL se está usando
// logger.debug("?? Axios config:", { hostname, protocol, baseURL });

const api = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  timeout: 30000,
});

const getCookieValue = (name: string) => {
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  const prefix = `${encodeURIComponent(name)}=`;
  const match = cookies.find((cookie) => cookie.startsWith(prefix));
  if (!match) return null;

  return decodeURIComponent(match.slice(prefix.length));
};

const getRefreshCsrfToken = () => getCookieValue("csrf_refresh_token");

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

const createCorrelationId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getGestorOrigin = () => {
  const pathname = window.location.pathname.toLowerCase();
  return pathname.includes("god") ? "god_mode" : "web_ui";
};

const getCorrelationId = (config: InternalAxiosRequestConfig) => {
  const existing = config.headers?.["X-Correlation-Id"];
  return typeof existing === "string" && existing ? existing : createCorrelationId();
};

// Interceptor para token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.headers) {
    config.headers["X-Gestor-Origin"] = getGestorOrigin();
    config.headers["X-Correlation-Id"] = getCorrelationId(config);

    if (config.url?.includes("/auth/refresh") || config.url?.includes("/logout")) {
      const csrfToken = getRefreshCsrfToken();
      if (csrfToken) {
        config.headers["X-CSRF-TOKEN"] = csrfToken;
      }
    }
  }

  return config;
});

// Interceptor de respuesta
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || "";
    const canTryRefresh =
      originalRequest &&
      !originalRequest._retry &&
      window.location.pathname !== "/login" &&
      !requestUrl.includes("/login") &&
      !requestUrl.includes("/auth/refresh") &&
      (status === 401 || status === 422);

    if (canTryRefresh) {
      originalRequest._retry = true;
      try {
        const csrfToken = getRefreshCsrfToken();
        const response = await axios.post(
          `${baseURL}/auth/refresh`,
          {},
          {
            withCredentials: true,
            timeout: 15000,
            headers: csrfToken ? { "X-CSRF-TOKEN": csrfToken } : undefined,
          }
        );
        const { access_token } = response.data || {};
        if (access_token) {
          setAccessToken(access_token);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        logger.warn("No se pudo renovar la sesión:", refreshError);
      }
    }

    // Si el error es 401/422 definitivo, limpiar token y redirigir al login
    if (status === 401 || status === 422) {
      logger.debug("🔐 Error 401 detectado:", {
        url: error.config?.url,
        method: error.config?.method,
        token: getAccessToken() ? "Presente" : "Ausente",
        errorData: error.response?.data,
      });

      // Solo redirigir si no estamos ya en la página de login
      if (window.location.pathname !== "/login") {
        // Limpiar token y datos de usuario
        clearAccessToken();
        localStorage.removeItem("user");

        // Redirigir al login
        window.location.href = "/login";

        // Mostrar mensaje al usuario
        alert("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
      }
    }

    return Promise.reject(error);
  }
);

export default api;
